import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  Activity,
  FileText
} from "lucide-react";

interface ViewDepartmentDialogProps {
  department: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewDepartmentDialog({ department, open, onOpenChange }: ViewDepartmentDialogProps) {
  if (!department) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Department Details</DialogTitle>
          <DialogDescription>Detailed view of the selected department</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Department Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {(department.name || 'N')[0]}
                  {(department.code || 'A')[0]}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-1">
                    {department.name || 'Department Name'}
                  </h1>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {/* Basic Information */}
              <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
                </div>
                
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <Building2 className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Department Name</p>
                      <p className="font-semibold text-gray-900">{department.name || "Not provided"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <Activity className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Status</p>
                      <Badge 
                        variant="outline" 
                        className={`${
                          department.status === "active" 
                            ? "bg-green-50 text-green-700 border-green-200" 
                            : department.status === "inactive"
                            ? "bg-red-50 text-red-700 border-red-200"
                            : "bg-gray-50 text-gray-700 border-gray-200"
                        } font-semibold`}
                      >
                        {department.status
                          ? department.status.charAt(0).toUpperCase() + department.status.slice(1)
                          : 'N/A'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Description</h2>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <FileText className="h-4 w-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-2">Department Description</p>
                  <p className="font-semibold text-gray-900 leading-relaxed">
                    {department.description || "No description provided"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 